import { BarChart3, Edit, Eye, Plus, Shield, TestTube, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PolicyAnalytics } from '@/components/PolicyAnalytics';
import { PolicyBuilder } from '@/components/PolicyBuilder';
import { PolicyTester } from '@/components/PolicyTester';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Policy {
  id: string;
  name: string;
  type: 'access' | 'upload' | 'download' | 'security';
  description: string;
  enabled: boolean;
  conditions: PolicyCondition[];
  actions: PolicyAction[];
  created_at: string;
  updated_at: string;
}

interface PolicyCondition {
  field: string;
  operator: 'equals' | 'contains' | 'matches' | 'gt' | 'lt';
  value: string;
}

interface PolicyAction {
  type: 'allow' | 'deny' | 'require_auth' | 'log' | 'notify';
  parameters?: Record<string, any>;
}

export function PoliciesPage() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTester, setShowTester] = useState(false);

  useEffect(() => {
    // Simulate loading policies
    setTimeout(() => {
      setPolicies([
        {
          id: '1',
          name: 'Require Authentication for Private Packages',
          type: 'access',
          description: 'Ensures all private package access requires valid authentication',
          enabled: true,
          conditions: [
            {
              field: 'package.visibility',
              operator: 'equals',
              value: 'private',
            },
          ],
          actions: [{ type: 'require_auth' }],
          created_at: '2024-01-15T10:30:00Z',
          updated_at: '2024-01-15T10:30:00Z',
        },
        {
          id: '2',
          name: 'Block Large Package Uploads',
          type: 'upload',
          description: 'Prevents upload of packages larger than 100MB',
          enabled: true,
          conditions: [{ field: 'package.size', operator: 'gt', value: '104857600' }],
          actions: [
            {
              type: 'deny',
              parameters: { message: 'Package size exceeds 100MB limit' },
            },
          ],
          created_at: '2024-01-10T14:20:00Z',
          updated_at: '2024-01-15T16:45:00Z',
        },
        {
          id: '3',
          name: 'Security Scanning',
          type: 'security',
          description: 'Automatically scan uploaded packages for known vulnerabilities',
          enabled: false,
          conditions: [{ field: 'package.uploaded', operator: 'equals', value: 'true' }],
          actions: [
            { type: 'log', parameters: { level: 'info' } },
            { type: 'notify', parameters: { webhook: 'security-team' } },
          ],
          created_at: '2024-01-05T09:15:00Z',
          updated_at: '2024-01-12T11:30:00Z',
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getPolicyTypeColor = (type: Policy['type']) => {
    switch (type) {
      case 'access':
        return 'bg-blue-100 text-blue-800';
      case 'upload':
        return 'bg-green-100 text-green-800';
      case 'download':
        return 'bg-purple-100 text-purple-800';
      case 'security':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCondition = (condition: PolicyCondition) => {
    return `${condition.field} ${condition.operator} "${condition.value}"`;
  };

  const formatAction = (action: PolicyAction) => {
    return action.type + (action.parameters ? ` (${Object.keys(action.parameters).join(', ')})` : '');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
          <p className="text-gray-500 mt-1">Loading policies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
          <p className="text-gray-500 mt-1">Manage access controls, security rules, and package policies</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            {showAnalytics ? 'Hide Analytics' : 'Analytics'}
          </Button>
          <Button variant="outline" onClick={() => setShowTester(true)} className="flex items-center gap-2">
            <TestTube className="w-4 h-4" />
            Test Policies
          </Button>
          <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Policy
          </Button>
        </div>
      </div>

      {showAnalytics && <PolicyAnalytics />}

      <div className="grid gap-4">
        {policies.length === 0 ? (
          <Card>
            <CardHeader className="text-center">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <CardTitle>No Policies Configured</CardTitle>
              <CardDescription>
                Create your first policy to start managing access controls and security rules
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2 mx-auto">
                <Plus className="w-4 h-4" />
                Create Your First Policy
              </Button>
            </CardContent>
          </Card>
        ) : (
          policies.map((policy) => (
            <Card key={policy.id} className="relative">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPolicyTypeColor(policy.type)}`}>
                      {policy.type}
                    </div>
                    <CardTitle className="text-lg">{policy.name}</CardTitle>
                    <div className={`w-2 h-2 rounded-full ${policy.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription>{policy.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Conditions</h4>
                    <div className="space-y-1">
                      {policy.conditions.map((condition, index) => (
                        <code key={index} className="block text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">
                          {formatCondition(condition)}
                        </code>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Actions</h4>
                    <div className="space-y-1">
                      {policy.actions.map((action, index) => (
                        <code key={index} className="block text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">
                          {formatAction(action)}
                        </code>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Created: {new Date(policy.created_at).toLocaleDateString()}</span>
                    <span>Updated: {new Date(policy.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {showCreateForm && (
        <PolicyBuilder
          onSave={(policy) => {
            const newPolicy: Policy = {
              id: Math.random().toString(36),
              ...policy,
              type: policy.type as Policy['type'],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setPolicies([...policies, newPolicy]);
            setShowCreateForm(false);
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {showTester && <PolicyTester onClose={() => setShowTester(false)} />}
    </div>
  );
}
